import { useEffect, useState, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { translations, LanguageContext } from "@/i18n";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import Marquee from "@/components/site/Marquee";
import Chapters from "@/components/site/Chapters";
import Overview from "@/components/site/Overview";
import Gallery from "@/components/site/Gallery";
import LocationSection from "@/components/site/LocationSection";
import BookingForm from "@/components/site/BookingForm";
import Footer from "@/components/site/Footer";
import ChatWidget from "@/components/site/ChatWidget";
import Availability from "@/components/site/Availability";
import Reviews from "@/components/site/Reviews";
import Experiences from "@/components/site/Experiences";
import Studio from "@/pages/Studio";
import Grazie from "@/pages/Grazie";
import LeaveReview from "@/pages/LeaveReview";
import Legal from "@/pages/Legal";
import Guida from "@/pages/Guida";
import Gift from "@/pages/Gift";
import LastMinuteBanner from "@/components/site/LastMinuteBanner";

const Home = () => (
  <main data-testid="home-page">
    <LastMinuteBanner />
    <Hero />
    <Marquee />
    <Chapters />
    <Overview />
    <Gallery />
    <LocationSection />
    <Availability />
    <Reviews />
    <Experiences />
    <BookingForm />
  </main>
);

function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("ci-lang") || "it");

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("ci-lang", lang);
  }, [lang]);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const value = useMemo(
    () => ({ lang, setLang, t: translations[lang], scrollTo }),
    [lang, scrollTo]
  );

  return (
    <LanguageContext.Provider value={value}>
      <div className="App min-h-screen bg-[#F8F5F0]">
        <BrowserRouter>
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/studio" element={<Studio />} />
            <Route path="/grazie" element={<Grazie />} />
            <Route path="/recensione/:token" element={<LeaveReview />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/cookie-policy" element={<Legal doc="cookie" />} />
            <Route path="/termini" element={<Legal doc="terms" />} />
            <Route path="/guida" element={<Guida />} />
            <Route path="/regalo" element={<Gift />} />
          </Routes>
          <Footer />
          <ChatWidget />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
